import {
  EditableReservoirParameter,
  ReservoirParameter,
  ReservoirType,
} from '~/shared/models/system/ReservoirModel';

// Fields in ui are displayed in the order defined below.
export class ReservoirParameterConfigModel {
  private static baseDisplayableParameters: Record<
    ReservoirType,
    ReservoirParameter[]
  > = {
    conventionalRoof: ['type', 'area'],
    blueRoof: [
      'capacityUsedPercentage',
      'capacityUsedVolume',
      'currentWaterLevel',
      'type',
      'area',
    ],
    greenRoof: [
      'capacityUsedPercentage',
      'capacityUsedVolume',
      'currentWaterLevel',
      'type',
      'area',
      'greenType',
      'areaGreen',
    ],
    tank: [
      'capacityUsedPercentage',
      'capacityUsedVolume',
      'currentWaterLevel',
      'type',
      'area',
    ],
    infiltrationTank: [
      'capacityUsedPercentage',
      'capacityUsedVolume',
      'currentWaterLevel',
      'type',
      'area',
    ],
    pond: [
      'capacityUsedPercentage',
      'capacityUsedVolume',
      'currentWaterLevel',
      'type',
      'area',
    ],
    wadi: [
      'capacityUsedPercentage',
      'capacityUsedVolume',
      'currentWaterLevel',
      'type',
      'area',
    ],
  };

  private static fullDisplayableParameters: Record<
    ReservoirType,
    ReservoirParameter[]
  > = {
    conventionalRoof:
      ReservoirParameterConfigModel.baseDisplayableParameters.conventionalRoof,
    blueRoof: [
      ...ReservoirParameterConfigModel.baseDisplayableParameters.blueRoof,
      'crateType',
      'minWaterHeight',
      'minWaterCapacityPercentage',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
      'factorHollow',
    ],
    greenRoof: [
      ...ReservoirParameterConfigModel.baseDisplayableParameters.greenRoof,
      'crateType',
      'minWaterHeight',
      'minWaterCapacityPercentage',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
      'factorHollow',
    ],
    tank: [
      ...ReservoirParameterConfigModel.baseDisplayableParameters.tank,
      'minWaterCapacityPercentage',
      'maxWaterHeight',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
      'factorHollow',
      'dryProtection',
      'externalUsage',
      'captureArea',
    ],
    infiltrationTank: [
      ...ReservoirParameterConfigModel.baseDisplayableParameters
        .infiltrationTank,
      'minWaterCapacityPercentage',
      'maxWaterHeight',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
      'factorHollow',
      'drainageSpeed',
    ],
    pond: [
      ...ReservoirParameterConfigModel.baseDisplayableParameters.pond,
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
      'pondNapOffset',
    ],
    wadi: [
      ...ReservoirParameterConfigModel.baseDisplayableParameters.wadi,
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
      'wadiMaxVolume',
      'drainageSpeed',
    ],
  };

  private static editableFields: Record<
    ReservoirType,
    EditableReservoirParameter[]
  > = {
    conventionalRoof: ['title', 'type', 'area'],
    blueRoof: [
      'title',
      'type',
      'crateType',
      'area',
      'factorHollow',
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
    ],
    greenRoof: [
      'title',
      'type',
      'crateType',
      'area',
      'factorHollow',
      'areaGreen',
      'greenType',
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
    ],
    tank: [
      'title',
      'type',
      'area',
      'factorHollow',
      'captureArea',
      'externalUsage',
      'minWaterCapacityPercentage',
      'maxWaterHeight',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
      'dryProtection',
    ],
    infiltrationTank: [
      'title',
      'type',
      'drainageSpeed',
      'area',
      'factorHollow',
      'minWaterCapacityPercentage',
      'maxWaterHeight',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
    ],
    pond: [
      'title',
      'type',
      'pondNapOffset',
      'area',
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
    ],
    wadi: [
      'title',
      'type',
      'drainageSpeed',
      'wadiMaxVolume',
      'area',
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
    ],
  };

  private static editableFieldsInAdviceSystemState: Record<
    ReservoirType,
    EditableReservoirParameter[]
  > = Object.fromEntries(
    Object.entries(ReservoirParameterConfigModel.editableFields).map(
      ([type, fields]) => [
        type,
        fields.filter(
          (field) =>
            ![
              'factorHollow',
              'minWaterHeight',
              'maxWaterHeight',
              'targetWaterHeight',
              'targetWaterHeightGrowingMonths',
            ].includes(field),
        ),
      ],
    ),
  ) as Record<ReservoirType, EditableReservoirParameter[]>;

  static getEditableFields({
    reservoirType,
    inAdviceSystemState = false,
  }: {
    reservoirType: ReservoirType;
    inAdviceSystemState?: boolean;
  }): EditableReservoirParameter[] {
    if (inAdviceSystemState) {
      return ReservoirParameterConfigModel.editableFieldsInAdviceSystemState[
        reservoirType
      ];
    }
    return ReservoirParameterConfigModel.editableFields[reservoirType];
  }

  static getDisplayableParameters({
    reservoirType,
    full = false,
  }: {
    reservoirType: ReservoirType;
    full?: boolean;
  }): ReservoirParameter[] {
    return full
      ? ReservoirParameterConfigModel.fullDisplayableParameters[reservoirType]
      : ReservoirParameterConfigModel.baseDisplayableParameters[reservoirType];
  }
}
